// useStoryDraft — the story board editor's networking and edit history (#1416).
//
// Page → hook → component: this hook owns every request and the undo stack;
// <StoryEditor> takes what it returns as props. Routes (PR A):
//
//   GET  /agent/artifacts/:id/story                 → source, versions, shas
//   PUT  /agent/artifacts/:id/story                 {source, base_version_id, note}
//        → {version_id, draft_sha256, warnings[]}
//        | 409 {code:"stale_base", latest_version_id}
//        | 422 {code:"story_invalid", error, details[]}
//   GET  /agent/artifacts/:id/story/versions/:vid   → {id, source}
//   POST /agent/artifacts/:id/publish               → {published_sha256, published_at, updated[], skipped[]}
//
// A save is a DRAFT: it never changes what a link viewer sees. Publish is the
// separate, explicit step that does.

import { useEffect, useRef, useState } from 'react'
import { initHistory, pushEdit, redo as redoHistory, undo as undoHistory, type StoryHistory } from '../story/storyHistory.js'
import { changedElements } from '../story/storyDiff.js'

/** How often the held lease is renewed (#1578 item 5: TTL 60s, renew every 20s). */
const LEASE_HEARTBEAT_MS = 20_000

export type StoryVersionId = string | number

export interface StoryVersion {
  id: StoryVersionId
  sha256: string
  size: number
  created_by: string
  note: string
  created_at: string | number
}

/** Where Publish writes: the share link, or a dashboard published from the same session. */
export interface PublishTarget {
  kind: string
  id: string | number
  name?: string
  /** GET only: false when Publish will skip it (someone else published it). */
  can_update?: boolean
  /** Publish result only: why it was skipped. */
  reason?: string
}

export interface StoryDraftResponse {
  /** The session the artifact belongs to. */
  session_id?: string
  source: string
  draft_sha256: string
  /** "" when never published. */
  published_sha256: string
  /** Unix seconds; 0 when never published. */
  published_at: string | number
  session_live: boolean
  /** False while a saved draft waits to be written into the paused sandbox. */
  draft_synced?: boolean
  versions: StoryVersion[]
  latest_version_id: StoryVersionId | null
  publish_targets?: PublishTarget[]
}

export interface StoryPublishResult {
  published_sha256: string
  published_at: string | number
  updated: PublishTarget[]
  skipped: PublishTarget[]
}

/** What the last save attempt came back with. */
export type StorySaveState =
  | { kind: 'idle' }
  | { kind: 'saved'; warnings: string[] }
  /** Someone (or the agent) saved a newer version since this draft was loaded. */
  | { kind: 'conflict'; latestVersionId: StoryVersionId | null }
  /** The server's check refused the page. */
  | { kind: 'invalid'; error: string; details: string[] }
  | { kind: 'error'; message: string }

export interface UseStoryDraftOptions {
  /** The web app artifact whose story.html is edited. Null while none is chosen. */
  artifactId: string | null
  /** API base URL. Default: '/api/v1'. */
  apiBaseUrl?: string
  /** Authorization header value (e.g. 'Bearer <token>'). */
  authHeader?: string
  /** Token provider, resolved on every request; wins over `authHeader`. */
  getAuthToken?: () => Promise<string> | string
  /** Injectable fetch, for tests and hosts that wrap requests. Default: global fetch. */
  fetchImpl?: typeof fetch
}

export interface UseStoryDraftResult {
  status: 'idle' | 'loading' | 'ready' | 'error'
  loadError: string | null
  /** The source the editor shows: the saved draft plus any unsaved edits. */
  source: string | null
  /** True when `source` differs from the last saved draft. */
  dirty: boolean
  /** Top-level blocks whose bytes differ from the last saved draft (#1578); empty while clean. */
  changedIdx: number[]
  /** Record an edit. Edits with the same `coalesceKey` in a row undo as one step. */
  applyEdit: (next: string, coalesceKey?: string) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean

  save: (note?: string) => Promise<boolean>
  saving: boolean
  saveState: StorySaveState
  /**
   * Load the newest saved draft. By default unsaved edits are thrown away (the
   * answer to a conflict). With `keepUnsaved`, a reload that lands while the
   * editor holds unsaved edits (typed during the fetch, say) leaves them alone.
   */
  reload: (opts?: { keepUnsaved?: boolean }) => Promise<void>
  /** The session the loaded story board belongs to; null until loaded. */
  sessionId: string | null

  versions: StoryVersion[]
  latestVersionId: StoryVersionId | null
  /** Put an earlier version back: it becomes a new edit, saved as a new version. */
  restore: (versionId: StoryVersionId) => Promise<boolean>
  restoring: boolean

  draftSha256: string
  publishedSha256: string
  publishedAt: string | number | null
  sessionLive: boolean
  /** False while a saved draft waits to reach the paused sandbox (it does with the next message). */
  draftSynced: boolean
  /** Where Publish would write, and whether each will be updated. */
  publishTargets: PublishTarget[]
  /** The saved draft is not what link viewers see. */
  draftDiffersFromPublished: boolean
  /**
   * Another live email holds the edit lease (#1578 item 5). Null while this
   * tab holds it (or no artifact is open). The editor goes read-only while
   * this is set, and unlocks itself once the holder's lease expires.
   */
  lockedBy: string | null

  /** Save any unsaved edits, then publish the draft to the share link and published dashboards. */
  publish: () => Promise<StoryPublishResult | null>
  publishing: boolean
  publishResult: StoryPublishResult | null
  publishError: string | null
}

async function readJson(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>
  } catch {
    return {}
  }
}

function errorText(body: Record<string, unknown>, res: Response): string {
  const e = body.error ?? body.message
  return typeof e === 'string' && e ? e : `HTTP ${res.status}`
}

export function useStoryDraft({
  artifactId,
  apiBaseUrl = '/api/v1',
  authHeader,
  getAuthToken,
  fetchImpl,
}: UseStoryDraftOptions): UseStoryDraftResult {
  const [status, setStatus] = useState<UseStoryDraftResult['status']>('idle')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [history, setHistory] = useState<StoryHistory | null>(null)
  const [savedSource, setSavedSource] = useState<string | null>(null)
  const [meta, setMeta] = useState<Omit<StoryDraftResponse, 'source'> | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveState, setSaveState] = useState<StorySaveState>({ kind: 'idle' })
  const [restoring, setRestoring] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishResult, setPublishResult] = useState<StoryPublishResult | null>(null)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [lockedBy, setLockedBy] = useState<string | null>(null)
  // The token this tab currently holds, outside React state: the heartbeat
  // interval and the pagehide/unmount release both need the newest value,
  // not the one captured when the effect was set up.
  const leaseTokenRef = useRef<string | null>(null)

  // Callbacks read the newest state through a ref: a save started from a
  // keyboard shortcut must send what is on screen, not what was on screen when
  // the handler was created.
  const latest = useRef({ history, savedSource, meta })
  latest.current = { history, savedSource, meta }
  // Bumped by every write that sets version/publish state, so a metadata
  // refresh that was already in flight cannot put older state back.
  const metaSeq = useRef(0)

  const base = artifactId ? `/agent/artifacts/${encodeURIComponent(artifactId)}` : ''

  async function request(path: string, init?: RequestInit): Promise<Response> {
    const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) }
    if (getAuthToken) {
      const token = await getAuthToken()
      if (token) headers['Authorization'] = `Bearer ${token}`
    } else if (authHeader) {
      headers['Authorization'] = authHeader
    }
    return (fetchImpl ?? fetch)(apiBaseUrl + base + path, { ...init, headers })
  }

  async function fetchDraft(signal?: AbortSignal): Promise<StoryDraftResponse> {
    const res = await request('/story', { signal })
    const body = await readJson(res)
    if (!res.ok) throw new Error(errorText(body, res))
    return body as unknown as StoryDraftResponse
  }

  function takeDraft(d: StoryDraftResponse) {
    metaSeq.current++
    const { source, ...rest } = d
    setMeta({ ...rest, versions: rest.versions ?? [] })
    setSavedSource(source)
    setHistory(initHistory(source))
  }

  // Load when the artifact changes; abort a load the user has navigated away from.
  useEffect(() => {
    if (!artifactId) {
      setStatus('idle')
      return
    }
    const ctrl = new AbortController()
    setStatus('loading')
    setLoadError(null)
    setSaveState({ kind: 'idle' })
    setPublishResult(null)
    setPublishError(null)
    fetchDraft(ctrl.signal)
      .then((d) => {
        if (ctrl.signal.aborted) return
        takeDraft(d)
        setStatus('ready')
      })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return
        setLoadError(e instanceof Error ? e.message : String(e))
        setStatus('error')
      })
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId])

  // Edit lease (#1578 item 5): acquired on load, renewed every 20s, given
  // back on unmount and on pagehide. No-op while no artifact is open.
  // 423 (another live email holds it) is not an error: `lockedBy` is set and
  // the same 20s tick keeps trying with no token, so the editor unlocks
  // itself the moment the holder's lease goes stale.
  useEffect(() => {
    leaseTokenRef.current = null
    if (!artifactId) {
      setLockedBy(null)
      return
    }
    let stopped = false

    async function acquire() {
      try {
        const res = await request('/story/lease', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leaseTokenRef.current ? { lease_token: leaseTokenRef.current } : {}),
        })
        if (stopped) return
        const body = await readJson(res)
        if (res.status === 423) {
          leaseTokenRef.current = null
          setLockedBy(typeof body.holder_email === 'string' ? body.holder_email : null)
          return
        }
        if (!res.ok) return // transient failure — the next heartbeat tries again
        leaseTokenRef.current = typeof body.lease_token === 'string' ? body.lease_token : null
        setLockedBy(null)
      } catch {
        // Offline or the request failed outright — try again next tick.
      }
    }

    function release(keepalive: boolean) {
      const token = leaseTokenRef.current
      if (!token) return
      leaseTokenRef.current = null
      void request('/story/lease', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lease_token: token }),
        keepalive,
      }).catch(() => {})
    }
    // sendBeacon cannot carry Authorization, so release goes through a
    // keepalive fetch instead (see #1578 item 5's API note).
    const onPageHide = () => release(true)

    void acquire()
    const timer = setInterval(() => { void acquire() }, LEASE_HEARTBEAT_MS)
    window.addEventListener('pagehide', onPageHide)

    return () => {
      stopped = true
      clearInterval(timer)
      window.removeEventListener('pagehide', onPageHide)
      release(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId])

  /** Refresh versions and shas after a write, without touching what the user is editing. */
  async function refreshMeta() {
    const seq = metaSeq.current
    try {
      const d = await fetchDraft()
      if (seq !== metaSeq.current) return
      const { source: _source, ...rest } = d
      setMeta({ ...rest, versions: rest.versions ?? [] })
    } catch {
      // The write succeeded; a stale version list is not worth an error.
    }
  }

  const applyEdit = (next: string, coalesceKey?: string) => {
    setHistory((h) => (h ? pushEdit(h, next, coalesceKey) : h))
  }
  const undo = () => setHistory((h) => (h ? undoHistory(h) : h))
  const redo = () => setHistory((h) => (h ? redoHistory(h) : h))

  async function putSource(source: string, note: string): Promise<boolean> {
    const { meta: m } = latest.current
    setSaving(true)
    try {
      const res = await request('/story', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, base_version_id: m?.latest_version_id ?? null, note }),
      })
      const body = await readJson(res)
      if (res.status === 423) {
        // Someone else took the lease between load and this save: the banner
        // (driven by `lockedBy`) says why, so no separate save error.
        setLockedBy(typeof body.holder_email === 'string' ? body.holder_email : null)
        return false
      }
      if (res.status === 409) {
        setSaveState({ kind: 'conflict', latestVersionId: (body.latest_version_id as StoryVersionId) ?? null })
        return false
      }
      if (res.status === 422) {
        setSaveState({
          kind: 'invalid',
          error: typeof body.error === 'string' ? body.error : 'The page did not pass the check.',
          details: Array.isArray(body.details) ? body.details.map(String) : [],
        })
        return false
      }
      if (!res.ok) {
        setSaveState({ kind: 'error', message: errorText(body, res) })
        return false
      }
      setSavedSource(source)
      metaSeq.current++
      setMeta((prev) =>
        prev
          ? {
              ...prev,
              latest_version_id: (body.version_id as StoryVersionId) ?? prev.latest_version_id,
              draft_sha256: typeof body.draft_sha256 === 'string' ? body.draft_sha256 : prev.draft_sha256,
              draft_synced: typeof body.draft_synced === 'boolean' ? body.draft_synced : prev.draft_synced,
            }
          : prev,
      )
      setSaveState({ kind: 'saved', warnings: Array.isArray(body.warnings) ? body.warnings.map(String) : [] })
      void refreshMeta()
      return true
    } catch (e) {
      setSaveState({ kind: 'error', message: e instanceof Error ? e.message : String(e) })
      return false
    } finally {
      setSaving(false)
    }
  }

  const save = async (note = ''): Promise<boolean> => {
    const { history: h } = latest.current
    if (!h) return false
    return putSource(h.present, note)
  }

  const reload = async (opts?: { keepUnsaved?: boolean }) => {
    try {
      const d = await fetchDraft()
      // Read after the fetch: edits typed while it was in flight count.
      const { history: h, savedSource: saved, meta: prevMeta } = latest.current
      if (opts?.keepUnsaved && h && h.present !== saved) {
        // Unsaved edits: never thrown away by a reload. But when the base
        // moved under them (#1578: the agent's own turn saved a newer
        // version) that is surfaced as the same conflict a stale save would
        // hit, not silently dropped — the host's "Load newest" banner is how
        // the reader finds out the page changed while they were typing.
        if (prevMeta && d.latest_version_id !== prevMeta.latest_version_id) {
          setSaveState({ kind: 'conflict', latestVersionId: d.latest_version_id ?? null })
        }
        return
      }
      takeDraft(d)
      setSaveState({ kind: 'idle' })
      setStatus('ready')
    } catch (e) {
      setSaveState({ kind: 'error', message: e instanceof Error ? e.message : String(e) })
    }
  }

  const restore = async (versionId: StoryVersionId): Promise<boolean> => {
    setRestoring(true)
    try {
      const res = await request(`/story/versions/${encodeURIComponent(String(versionId))}`)
      const body = await readJson(res)
      if (!res.ok || typeof body.source !== 'string') {
        setSaveState({ kind: 'error', message: errorText(body, res) })
        return false
      }
      const source = body.source
      // An edit like any other (undo takes it back), then saved: a restore is
      // recorded as a NEW version, the old ones stay.
      setHistory((h) => (h ? pushEdit(h, source) : initHistory(source)))
      return await putSource(source, `Restored version ${versionId}`)
    } catch (e) {
      setSaveState({ kind: 'error', message: e instanceof Error ? e.message : String(e) })
      return false
    } finally {
      setRestoring(false)
    }
  }

  const publish = async (): Promise<StoryPublishResult | null> => {
    const { history: h, savedSource: saved } = latest.current
    setPublishError(null)
    setPublishResult(null)
    if (h && h.present !== saved) {
      const ok = await putSource(h.present, '')
      if (!ok) return null
    }
    setPublishing(true)
    try {
      const res = await request('/publish', { method: 'POST' })
      const body = await readJson(res)
      if (res.status === 423) {
        setLockedBy(typeof body.holder_email === 'string' ? body.holder_email : null)
        return null
      }
      if (!res.ok) {
        setPublishError(errorText(body, res))
        return null
      }
      const result: StoryPublishResult = {
        published_sha256: String(body.published_sha256 ?? ''),
        published_at: (body.published_at as string | number) ?? '',
        updated: Array.isArray(body.updated) ? (body.updated as PublishTarget[]) : [],
        skipped: Array.isArray(body.skipped) ? (body.skipped as PublishTarget[]) : [],
      }
      setPublishResult(result)
      metaSeq.current++
      setMeta((prev) => (prev ? { ...prev, published_sha256: result.published_sha256, published_at: result.published_at } : prev))
      return result
    } catch (e) {
      setPublishError(e instanceof Error ? e.message : String(e))
      return null
    } finally {
      setPublishing(false)
    }
  }

  const source = history?.present ?? null
  const draftSha256 = meta?.draft_sha256 ?? ''
  const publishedSha256 = meta?.published_sha256 ?? ''
  const publishTargets = meta?.publish_targets ?? []
  const everPublished = !!meta && Number(meta.published_at) > 0

  return {
    status,
    loadError,
    source,
    dirty: source !== null && source !== savedSource,
    changedIdx: source != null && savedSource != null ? changedElements(savedSource, source) : [],
    applyEdit,
    undo,
    redo,
    canUndo: (history?.past.length ?? 0) > 0,
    canRedo: (history?.future.length ?? 0) > 0,
    save,
    saving,
    saveState,
    reload,
    sessionId: meta?.session_id ?? null,
    versions: meta?.versions ?? [],
    latestVersionId: meta?.latest_version_id ?? null,
    restore,
    restoring,
    draftSha256,
    publishedSha256,
    publishedAt: meta?.published_at || null,
    sessionLive: !!meta?.session_live,
    draftSynced: meta?.draft_synced ?? true,
    publishTargets,
    // Never published but shared or on a dashboard: those still show the
    // page as it was before any edit, so the draft differs from them too.
    draftDiffersFromPublished: everPublished ? draftSha256 !== publishedSha256 : publishTargets.length > 0,
    lockedBy,
    publish,
    publishing,
    publishResult,
    publishError,
  }
}

export default useStoryDraft
